import type { PdfDocument, PdfPage } from '@syncfusion/ej2-pdf';

/**
 * Spec-correct appearance stamping for form fields on rotated pages.
 *
 * ej2-pdf's field flatten mis-transforms appearance stamps on pages with /Rotate 90: its
 * rotation compensation lands every stamp outside the media box (values silently vanish),
 * and its anchoring uses the appearance BBox's raw height instead of mapping the
 * Matrix-transformed BBox onto the widget /Rect (PDF 32000-1 8.4.4), so even a corrected
 * translation would shift each field by rect-height minus BBox-height. Both defects are
 * present through at least ej2-pdf 34.1.31.
 *
 * The workaround: for /Rotate 90 pages, stamp each widget's /AP /N stream into the page
 * content ourselves with the spec's rect-mapping matrix, inserted at a point in the
 * /Contents array where the CTM is pristine (after the global save/restore pair ej2 wraps
 * the original content in, before ej2's own graphics stream). ej2's flatten still runs so
 * the widget annotations are removed; its misplaced stamps land off-page (provably: their
 * device x is page-height plus the field offset, beyond a portrait media box) and stay
 * invisible.
 */

export interface ITopLeftBounds {
    x: number;
    y: number;
    width: number;
    height: number;
}

export interface IFieldAppearanceStamp {
    /** Indirect reference of the /AP /N form XObject stream. */
    appearanceRef: unknown;
    /** Content-stream matrix [a b c d e f] mapping the appearance onto the widget rect. */
    matrix: [number, number, number, number, number, number];
}

interface IPdfReferenceLike {
    objectNumber?: number;
}

interface IPdfDictionaryLike {
    has?: (key: string) => boolean;
    get?: (key: string) => unknown;
    getRaw?: (key: string) => unknown;
    getArray?: (key: string) => unknown;
    update?: (key: string, value: unknown) => void;
    set?: (key: string, value: unknown) => void;
    _updated?: boolean;
}

interface IPdfStreamLike {
    dictionary?: IPdfDictionaryLike;
}

interface IPdfCrossReferenceLike {
    _fetch?: (ref: unknown) => unknown;
    _getNextReference?: () => unknown;
    _cacheMap?: Map<unknown, unknown>;
}

interface IPdfDocumentInternals {
    _crossReference?: IPdfCrossReferenceLike;
}

interface IPdfPageStampingInternals {
    _pageDictionary?: IPdfDictionaryLike;
    _contents?: unknown[];
    graphics?: unknown;
}

interface IPdfFieldInternals {
    _dictionary?: IPdfDictionaryLike;
}

const PDF_ANNOTATION_FLAG_HIDDEN = 2;
const PDF_ANNOTATION_FLAG_NO_VIEW = 32;

/** /Rotate normalized to 0/90/180/270; 0 when absent or unreadable. */
export const getPageRotationDegrees = (page: PdfPage): number => {
    try {
        const dictionary = (page as unknown as IPdfPageStampingInternals)._pageDictionary;
        const raw = dictionary?.get?.('Rotate');
        if (typeof raw !== 'number' || !Number.isFinite(raw)) {
            return 0;
        }
        const normalized = (((Math.round(raw / 90) * 90) % 360) + 360) % 360;
        return normalized;
    } catch {
        return 0;
    }
};

/**
 * Only 90° pages take the custom-stamping path: it is the rotation Corptax landscape pages
 * use, the display mapping below is derived for it, and ej2's misplaced flatten stamps are
 * proven to fall outside the media box for it (so letting ej2's flatten run stays safe).
 */
export const isStampableRotation = (rotationDegrees: number): boolean => rotationDegrees === 90;

/**
 * Maps widget bounds (PDF points, top-left origin, unrotated page space) into the space of
 * the page as the viewer displays it, for a /Rotate 90 (clockwise) page. The displayed page
 * is pageSize[1] wide, so hitbox scaling must divide by that width.
 */
export const mapTopLeftBoundsForRotation90 = (bounds: ITopLeftBounds, pageSize: number[]): ITopLeftBounds => ({
    x: pageSize[1] - bounds.y - bounds.height,
    y: bounds.x,
    width: bounds.height,
    height: bounds.width,
});

const isReferenceLike = (value: unknown): boolean => !!value && typeof (value as IPdfReferenceLike).objectNumber === 'number';

const toNumberArray = (value: unknown, expectedLength: number): number[] | null => {
    if (!Array.isArray(value) || value.length < expectedLength) {
        return null;
    }
    const numbers = value.slice(0, expectedLength).map((entry) => (typeof entry === 'number' ? entry : Number.NaN));
    return numbers.some((entry) => !Number.isFinite(entry)) ? null : numbers;
};

const formatOperand = (value: number): string => {
    const rounded = Number(value.toFixed(4));
    return Object.is(rounded, -0) ? '0' : rounded.toString();
};

/**
 * Builds the stamp for one widget: resolves its /AP /N stream reference and computes the
 * matrix that maps the appearance's Matrix-transformed BBox onto the widget /Rect
 * (PDF 32000-1 8.4.4 algorithm: transform BBox corners, take the bounding box, scale and
 * translate it onto the rect). Returns null when the field has no usable authored
 * appearance or is hidden — the caller then leaves the field to the default handling.
 */
export const collectFieldAppearanceStamp = (document: PdfDocument, field: unknown): IFieldAppearanceStamp | null => {
    try {
        const crossReference = (document as unknown as IPdfDocumentInternals)._crossReference;
        const dictionary = (field as IPdfFieldInternals)._dictionary;
        if (!crossReference?._fetch || !dictionary) {
            return null;
        }

        const flags = dictionary.get?.('F');
        if (typeof flags === 'number' && (flags & (PDF_ANNOTATION_FLAG_HIDDEN | PDF_ANNOTATION_FLAG_NO_VIEW)) !== 0) {
            return null;
        }

        const rectRaw = toNumberArray(dictionary.getArray?.('Rect'), 4);
        if (!rectRaw) {
            return null;
        }
        const rect = {
            llx: Math.min(rectRaw[0], rectRaw[2]),
            lly: Math.min(rectRaw[1], rectRaw[3]),
            width: Math.abs(rectRaw[2] - rectRaw[0]),
            height: Math.abs(rectRaw[3] - rectRaw[1]),
        };
        if (rect.width <= 0 || rect.height <= 0) {
            return null;
        }

        let appearanceDictionary = dictionary.getRaw?.('AP');
        if (isReferenceLike(appearanceDictionary)) {
            appearanceDictionary = crossReference._fetch(appearanceDictionary);
        }
        const appearanceRef = (appearanceDictionary as IPdfDictionaryLike | undefined)?.getRaw?.('N');
        if (!isReferenceLike(appearanceRef)) {
            return null;
        }
        const appearanceStream = crossReference._fetch(appearanceRef) as IPdfStreamLike | undefined;
        const streamDictionary = appearanceStream?.dictionary;
        const bbox = toNumberArray(streamDictionary?.getArray?.('BBox'), 4);
        if (!bbox) {
            return null;
        }
        const matrix = toNumberArray(streamDictionary?.getArray?.('Matrix'), 6) ?? [1, 0, 0, 1, 0, 0];

        const corners: [number, number][] = [
            [bbox[0], bbox[1]],
            [bbox[2], bbox[1]],
            [bbox[0], bbox[3]],
            [bbox[2], bbox[3]],
        ];
        const transformed = corners.map(([x, y]): [number, number] => [
            matrix[0] * x + matrix[2] * y + matrix[4],
            matrix[1] * x + matrix[3] * y + matrix[5],
        ]);
        const xs = transformed.map((corner) => corner[0]);
        const ys = transformed.map((corner) => corner[1]);
        const transformedWidth = Math.max(...xs) - Math.min(...xs);
        const transformedHeight = Math.max(...ys) - Math.min(...ys);
        if (transformedWidth <= 0 || transformedHeight <= 0) {
            return null;
        }

        const scaleX = rect.width / transformedWidth;
        const scaleY = rect.height / transformedHeight;
        const translateX = rect.llx - scaleX * Math.min(...xs);
        const translateY = rect.lly - scaleY * Math.min(...ys);
        return {
            appearanceRef,
            matrix: [scaleX, 0, 0, scaleY, translateX, translateY],
        };
    } catch {
        return null;
    }
};

let stampNameCounter = 0;

/**
 * Appends one content stream holding all of a page's appearance stamps, positioned in the
 * /Contents array immediately before ej2's own appended graphics stream — after the global
 * restore, so the ops execute at the pristine base CTM in raw (bottom-left) page space.
 * Registers each appearance stream as a page XObject resource. Fails soft: on any internal
 * mismatch nothing is appended and the page renders as it would have without this call.
 */
export const appendAppearanceStamps = (document: PdfDocument, page: PdfPage, stamps: IFieldAppearanceStamp[]): boolean => {
    if (stamps.length === 0) {
        return false;
    }
    try {
        const crossReference = (document as unknown as IPdfDocumentInternals)._crossReference;
        const pageInternals = page as unknown as IPdfPageStampingInternals;
        if (!crossReference?._fetch || !crossReference._getNextReference || !crossReference._cacheMap) {
            return false;
        }

        // Touching graphics makes ej2 rebuild /Contents as [save, ...original, restore,
        // graphics] and mark the page dictionary updated — the shape the insertion relies on.
        void (page as unknown as { graphics?: unknown }).graphics;
        const contents = pageInternals._contents;
        const pageDictionary = pageInternals._pageDictionary;
        if (!Array.isArray(contents) || contents.length < 2 || !pageDictionary) {
            return false;
        }
        const ej2GraphicsRef = contents[contents.length - 1];
        const contentStreamInstance = crossReference._cacheMap.get(ej2GraphicsRef);
        if (!contentStreamInstance) {
            return false;
        }
        const ContentStreamConstructor = (contentStreamInstance as { constructor: new (bytes: number[]) => unknown }).constructor;

        // Resolve the page's /Resources (direct or indirect) and its /XObject subdictionary.
        let resources = pageDictionary.getRaw?.('Resources');
        if (isReferenceLike(resources)) {
            resources = crossReference._fetch(resources);
        }
        const resourcesDictionary = resources as IPdfDictionaryLike | undefined;
        if (!resourcesDictionary?.update) {
            return false;
        }
        let xObjects = resourcesDictionary.getRaw?.('XObject');
        if (isReferenceLike(xObjects)) {
            xObjects = crossReference._fetch(xObjects);
        }
        let xObjectsDictionary = xObjects as IPdfDictionaryLike | undefined;
        if (typeof xObjectsDictionary?.update !== 'function') {
            // No usable /XObject subdictionary — build one. _PdfDictionary is not part of
            // the public API, so reuse the resources dictionary's constructor.
            const DictionaryConstructor = (resourcesDictionary as unknown as { constructor: new () => IPdfDictionaryLike }).constructor;
            const createdDictionary = new DictionaryConstructor();
            if (typeof createdDictionary?.update !== 'function') {
                return false;
            }
            xObjectsDictionary = createdDictionary;
            resourcesDictionary.update('XObject', xObjectsDictionary);
        }

        let operators = '';
        for (const stamp of stamps) {
            const name = `CTXAP${stampNameCounter++}`;
            xObjectsDictionary.update?.(name, stamp.appearanceRef);
            operators += `q\n${stamp.matrix.map(formatOperand).join(' ')} cm\n/${name} Do\nQ\n`;
        }
        resourcesDictionary._updated = true;

        const bytes: number[] = [];
        for (let index = 0; index < operators.length; index++) {
            bytes.push(operators.charCodeAt(index));
        }
        const stampStream = new ContentStreamConstructor(bytes);
        const stampRef = crossReference._getNextReference();
        crossReference._cacheMap.set(stampRef, stampStream);
        contents.splice(contents.length - 1, 0, stampRef);
        pageDictionary.set?.('Contents', contents);
        pageDictionary._updated = true;
        return true;
    } catch {
        return false;
    }
};
